import { Component, ElementRef, OnDestroy, ViewChild, Inject, Optional } from '@angular/core';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { assetsConst } from 'src/app/core/const/assets.const';
import { LoginComponent } from '../login/login.component';
import { VerificationComponent } from '../verification/verification.component';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { MustMatch } from 'src/app/core/helpers/must-match.validator';
import { AuthService } from 'src/app/core/services/auth.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { REGEX } from 'src/app/core/const/regex.constant';
import { CountryCodeConst } from 'src/app/core/const/countryCodeConst.const';
import { countryCode } from 'src/app/models/countryCode';
import { ProfileDetails, signUpBody } from 'src/app/models/authmodel';
import { checkValidator } from 'src/app/core/helpers/checkpasswordPatternvalidation.helper';
import { passwordFormErrors } from 'src/app/core/const/passwordFormErrors.const';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { GoogleSigninButtonModule, SocialAuthService } from '@abacritt/angularx-social-login';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'megastore-signup',
  templateUrl: './signup.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    NgxMatIntlTelInputComponent,
    MatDialogModule,
    GoogleSigninButtonModule
  ],
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('phone') phoneField!: ElementRef | any;
  hiddenPasswordVisibility = true;
  hiddenConfirmPasswordVisibility = true;
  submitted = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  assetConst = assetsConst;
  countryCodeObject: countryCode = CountryCodeConst;
  signUpFormGroup: FormGroup;
  isRemembered = false;
  signUpWithEmail = true;
  checkValidations = checkValidator;
  formErrors = passwordFormErrors;

  constructor(
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<SignupComponent>,
    private formBuilder: FormBuilder,
    private _authService: AuthService,
    private _toasterService: ToasterService,
    private _translateService: TranslateService,
    private _spinnerService: SpinnerService,
    private _socialLoginService: SocialAuthService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.signUpFormGroup = this.formBuilder.group(
      {
        email: ['', [Validators.required, Validators.pattern(REGEX.Email)]],
        phoneNo: [
          {
            value: '',
            disabled: true
          }
        ],
        dialCode: [
          {
            value: '',
            disabled: true
          }
        ],
        password: [
          '',
          [Validators.required, Validators.pattern(REGEX.Password)]
        ],
        confirmPassword: ['', Validators.required]
      },
      { validators: MustMatch('password', 'confirmPassword') }
    );

    this.socialLogin();
    
    // Pre-populate form if data is provided (when coming back from verification)
    if (this.data) {
      this.prePopulateForm();
    }
  }

  prePopulateForm(): void {
    console.log('data',this.data);
    if (this.data.email) {
      this.signUpWithEmail = true;
      this.signUpFormGroup.patchValue({
        email: this.data.email,
        password: this.data.password,
        confirmPassword: this.data.password
      });
    } else if (this.data.phoneNo && this.data.dialCode) {
      this.signUpWithEmail = false;
      this.signUpFormGroup.patchValue({
        phoneNo: this.data.phoneNo,
        dialCode: this.data.dialCode,
        password: this.data.password,
        confirmPassword: this.data.password
      });
    }
    this.loginTypeChanged(true);
  }

  toggleRememberMe(event: MatCheckboxChange): void {
    this.isRemembered = event['checked'];
  }

  socialLogin(): void {
    this._socialLoginService.authState.subscribe(user => {
      const userData: {
        name: string;
        email: string;
        googleId: string;
      } = {
        name: user.name,
        email: user.email,
        googleId: user.id
      };
      this._authService
        .socialLogin(userData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: ProfileDetails) => {
            localStorage.setItem(environment.storageKey, response.accessToken);
            sessionStorage.removeItem(environment.guestTokenKey);
            this._toasterService.successToastr(
              this._translateService.instant('LoginSuccess')
            );
            this._authService.profileDetails.next(response);
            this._authService.loggedIn.next(true);
            this.dialogRef.close(true);
          },
          error: () => {
            this._spinnerService.removeFromLoader('login');
          }
        });
    });
  }

  onSubmit(): void {
    this.submitted = true;
    const modifiedValue = this.patchValues();

    if (this.signUpFormGroup.valid) {
      this.onFormValid(modifiedValue);
    }
  }

  onFormValid(payload: signUpBody): void {                                                                                                                                                    
    this._spinnerService.addToLoader('signUp');
    this._authService
      .signUpUser(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this._spinnerService.removeFromLoader('signUp');
          this._toasterService.successToastr(
            this._translateService.instant('otpSentSuccesss')
          );
          this.dialogRef.close();
          const data = payload;
          delete data.confirmPassword;
          this.dialog.open(VerificationComponent, {
            panelClass: ['modall', 'login_nodal'],
            data: {
              ...data,
              length: 4,
              type: 'signUp'
            }
          });
        },
        error: () => {
          this._spinnerService.removeFromLoader('signUp');
        }
      });
  }

  openLoginDialog(): void {
    this.dialogRef.close();
    this.dialog.open(LoginComponent, {
      panelClass: ['modall', 'login_nodal']
    });
  }

  loginTypeChanged(isPrePopulate: boolean = false): void {
    if (!isPrePopulate) {
      this.signUpWithEmail = !this.signUpWithEmail;
    }
    this.submitted = false;

    if (this.signUpWithEmail) {
      this.signUpFormGroup.get('email')?.enable();
      this.signUpFormGroup.get('password')?.enable();
      this.signUpFormGroup.get('confirmPassword')?.enable();

      this.signUpFormGroup
        .get('email')
        ?.setValidators([Validators.required, Validators.pattern(REGEX.Email)]);

      this.signUpFormGroup
        .get('password')
        ?.setValidators([
          Validators.required,
          Validators.pattern(REGEX.Password)
        ]);

      this.signUpFormGroup
        .get('confirmPassword')
        ?.setValidators(Validators.required);

      // ** has to discuss about the confirm & password field mustMatch validation on change **

      // this.signUpFormGroup.setValidators({
      //   validators: MustMatch('password', 'confirmPassword')
      // });

      this.signUpFormGroup.get('dialCode')?.disable();
      this.signUpFormGroup.get('phoneNo')?.disable();

      this.signUpFormGroup.updateValueAndValidity();
    } else {
      this.signUpFormGroup.get('email')?.disable();
      this.signUpFormGroup.get('password')?.disable();
      this.signUpFormGroup.get('confirmPassword')?.disable();
      this.signUpFormGroup.get('dialCode')?.enable();
      this.signUpFormGroup.get('phoneNo')?.enable();
      this.signUpFormGroup.get('phoneNo')?.setValidators([Validators.required]);

      this.signUpFormGroup.updateValueAndValidity();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countryChangedEvent(event: any): void {
    this.countryCodeObject = event;
  }

  patchValues(): signUpBody {
    // eslint-disable-next-line prefer-const
    let value: signUpBody;

    const patchValueToCountryCode = (): void => {
      let tempCountryCode = this.signUpFormGroup.get('dialCode')?.value;

      if (tempCountryCode) {
        tempCountryCode = tempCountryCode.split('');
        tempCountryCode.shift();
        tempCountryCode = tempCountryCode.join('');
      }

      if (tempCountryCode !== this.countryCodeObject.dialCode) {
        this.signUpFormGroup.patchValue({
          dialCode: `+${this.countryCodeObject.dialCode}`
        });
      }
    };

    const patchValueToPhoneField = (): void => {
      let phone = this.signUpFormGroup.get('phoneNo')?.value;

      if (
        phone &&
        phone.includes(this.signUpFormGroup.get('dialCode')?.value)
      ) {
        phone = phone.split(this.signUpFormGroup.get('dialCode')?.value);
        if (value.phoneNo !== undefined) {
          value.phoneNo = phone[1].trim();
        }
      }
    };

    patchValueToCountryCode();
    value = this.signUpFormGroup['value'];
    patchValueToPhoneField();

    return value;
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
}